import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  useDeleteEmailTemplateMutation,
  useGetEmailTemplateQuery,
} from "../../../Api/attoDeskApi";
import { IEmailTemplate } from "../../../Api/Interface/api.interface";
import NewPopUpEmail from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AddIcon from "@mui/icons-material/Add";
import Pagination from "../../../Components/Pagination";

const stripHtml = (html: string) => {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return tmp.textContent || tmp.innerText || "";
};

const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [rowsPerPage] = useState(25);
  const [selectedCoursing, setSelectedCoursing] = useState<IEmailTemplate | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IEmailTemplate | null>(null);

  const { data, isLoading, isError, refetch } = useGetEmailTemplateQuery({
    searchText: currentSearchQuery,
  });

  useEffect(() => {
    if (debouncedSearchQuery.length >= 3 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleOpenDialog = (row: IEmailTemplate) => {
    setSelectedCoursing(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (row: IEmailTemplate) => {
    setCoursingToDelete(row);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteEmailTemplateMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Template deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the template");
      }
    } catch {
      showErrorMessage("Failed to delete the template");
    }
  };

  const coursings: IEmailTemplate[] = Array.isArray(data?.data)
    ? (data?.data as IEmailTemplate[])
    : [];

  const filtered = coursings.filter((t) =>
    t.template_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ─── header cell style ───────────────────────────────────────────────────────
  const thSx = {
    fontWeight: 700,
    fontSize: 11,
    color: "#64748b",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    py: 1.5,
    px: 2,
    borderBottom: "1px solid #e2e8f0",
    bgcolor: "#f8fafc",
  };

  const tdSx = { py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: "linear-gradient(135deg,#2f3891,#2f3891)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EmailOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
              Email Templates
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
              {filtered.length} template{filtered.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search templates…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ color: "#94a3b8", fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 13, bgcolor: "#fff", minWidth: 220 },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/email/create")}
            sx={{
              background: "linear-gradient(135deg,#2f3891,#2f3891)",
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              px: 2.5,
              boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#2f3891,#2f3891)" },
            }}
          >
            Add Template
          </Button>
        </Box>
      </Box>

      {/* ── Table card ───────────────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#2f3891" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>#</TableCell>
                  <TableCell sx={thSx}>Template</TableCell>
                  <TableCell sx={thSx}>Subject</TableCell>
                  <TableCell sx={thSx}>Preview</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <EmailOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                        <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                          {isError ? "Error fetching templates" : searchQuery ? "No templates match your search" : "No email templates yet"}
                        </Typography>
                        {!isError && !searchQuery && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate("/email/create")}
                            sx={{ mt: 2, borderRadius: 2, textTransform: "none", color: "#2f3891", borderColor: "#2f3891" }}
                          >
                            Create first template
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((row: IEmailTemplate, index) => {
                    const preview = stripHtml(row.message);
                    const initials = row.template_name
                      ?.split(" ")
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase();

                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          "&:hover": { bgcolor: "#fafcff" },
                          "&:last-child td": { borderBottom: 0 },
                        }}
                      >
                        {/* # */}
                        <TableCell sx={{ ...tdSx, color: "#94a3b8", fontSize: 12, width: 48 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        {/* Template name */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 34,
                                height: 34,
                                fontSize: 12,
                                fontWeight: 700,
                                background: "linear-gradient(135deg,#2f3891,#2f3891)",
                              }}
                            >
                              {initials}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
                                {row.template_name}
                              </Typography>
                              {row.is_mandatory === 1 && (
                                <Chip
                                  label="System"
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    bgcolor: "#fef3c7",
                                    color: "#92400e",
                                    border: "1px solid #fde68a",
                                    mt: 0.3,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Subject */}
                        <TableCell sx={tdSx}>
                          <Typography sx={{ fontSize: 13, color: "#475569", fontWeight: 500, maxWidth: 220 }}>
                            {row.subject || "—"}
                          </Typography>
                        </TableCell>

                        {/* Message preview */}
                        <TableCell sx={tdSx}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#94a3b8",
                              maxWidth: 280,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {preview ? preview.slice(0, 80) + (preview.length > 80 ? "…" : "") : "—"}
                          </Typography>
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                            <Tooltip title="Edit template" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(row)}
                                sx={{
                                  color: "#2f3891",
                                  bgcolor: "#eef2ff",
                                  borderRadius: 1.5,
                                  width: 30,
                                  height: 30,
                                  "&:hover": { bgcolor: "#e0e7ff" },
                                }}
                              >
                                <EditSharpIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            {row.is_mandatory === 0 && (
                              <Tooltip title="Delete template" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDeletePopup(row)}
                                  sx={{
                                    color: "#ef4444",
                                    bgcolor: "#fef2f2",
                                    borderRadius: 1.5,
                                    width: 30,
                                    height: 30,
                                    "&:hover": { bgcolor: "#fee2e2" },
                                  }}
                                >
                                  <DeleteOutlineSharpIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Pagination ────────────────────────────────────────────────────── */}
        {filtered.length > rowsPerPage && (
          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
            />
          </Box>
        )}
      </Box>

      {/* ── Dialogs ──────────────────────────────────────────────────────────── */}
      {selectedCoursing && (
        <NewPopUpEmail
          openModel={openDialog}
          handleCloseDialog={handleCloseDialog}
          data={selectedCoursing}
        />
      )}
      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => {
            await handleDelete(coursingToDelete.id.toString());
          }}
          title="Delete Template"
          content={`Are you sure you want to delete "${coursingToDelete.template_name}"?`}
        />
      )}
    </Box>
  );
};

export default Index;
